import { useReducer, useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';

/**
 * Contexts
 */
import { AppContext } from '../../contexts/AppContext';
import { SharedFormContext } from '../../contexts/SharedFormContext';
import { ReportFormContext } from '../../contexts/ReportFormContext';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';

/**
 * Models
 */
import { Extra } from '../../models/WorkReportSubmodels';

/**
 * MUI Components
 */
import { Button, Box, TextField, TextareaAutosize } from '@mui/material';

/**
 * Components
 */
import SpeciesSelectControl from './Fields/SpeciesSelectControl';
import WorkActionAreaControl from './Fields/WorkActionAreaControl';
import WorkActionSubareaControl from './Fields/WorkActionSubareaControl';
import ExtraActionSelectControl from './Fields/ExtraActionSelectControl';
import ExtraStatusSelectControl from './Fields/ExtraStatusSelectControl';

const styles = {
    formBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '300px',
        margin: '0 auto'
    }
};

// Local initial state
const initialState = {
    selectSpecies: 0,
    selectActions: 0,
    selectStatuses: 0,
    selectActionArea: 0,
    selectActionSubarea: 0,
    filteredSpecies: [],
    filteredActions: [],
    filteredStatuses: [],
    filteredActionArea: [],
    filteredActionSubarea: [],
    isFormValid: false
};

// Local state reducer
const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_FORM_DATA':
            if (action.name === 'all') {
                return { ...state, ...action.value };
            }
            return { ...state, [action.name]: action.value };
        case 'SET_FILTERED_SPECIES':
            return {
                ...state,
                filteredSpecies: action.filteredSpecies,
            };
        case 'SET_FILTERED_ACTIONS':
            return {
                ...state,
                filteredActions: action.filteredActions,
            };
        case 'SET_FILTERED_STATUSES':
            return {
                ...state,
                filteredStatuses: action.filteredStatuses,
            };
        case 'SET_FILTERED_ACTION_AREA':
            return {
                ...state,
                filteredActionArea: action.filteredActionArea,
            };
        case 'SET_FILTERED_ACTION_SUBAREA':
            return {
                ...state,
                filteredActionSubarea: action.filteredActionSubarea,
            };
        case 'SET_FORM_VALIDITY':
            return {
                ...state,
                isFormValid: action.isFormValid
            };
        default:
            return state;
    }
};

const AddExtrasForm = ({ onSubmit, planning, oldData }) => {
    const { t } = useTranslation();
    const { control, getValues, reset, formState, trigger } = useForm({
        mode: 'onChange', // Set validation mode
    });

    const { isValid, isDirty } = formState;
    const appData = useContext(AppContext);
    const { report, setReport } = useContext(ReportFormContext);
    const { updateSharedValue, getSharedValue } = useContext(SharedFormContext);
    const [state, dispatch] = useReducer(reducer, initialState);
    const isEdit = !!oldData;
    const uid = appData.userInfo?.id || null;

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        // For Switch components, use the checked value instead of value
        const newValue = name === 'exclude_from_billing' ? checked : value;
        dispatch({ type: 'SET_FORM_DATA', name, value: newValue });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = getValues();
        // console.log("Data from extra form:", formData);

        const extra = Extra.new();
        extra.species_id = formData.species;
        extra.action_id = formData.action;
        extra.status_id = formData.status;
        extra.feet = formData.feet;
        extra.notes = formData.notes;
        extra.workActionAreaId = state.selectActionArea;
        extra.workActionSubareaId = state.selectActionSubarea;
        extra.owner_id = uid || 0;

        if (isEdit) {
            // Modify existing extra record!
            let index = report.extras.indexOf(oldData);
            report.extras[index] = extra;
            setReport({ ...report });
            console.log('Extra record updated with index', index);
        } else {
            // Add new extra record!
            report.extras.push(extra);
            setReport({ ...report });
            console.log('New extra record added');
            // console.log(report);

            // Update shared data between forms
            updateSharedValue('selectActionArea', state.selectActionArea);
            updateSharedValue('selectActionSubarea', state.selectActionSubarea);
        }

        // Call onSubmit
        onSubmit();
    };

    // Autoset initial state from shared data if exists
    useEffect(() => {
        if (isEdit && oldData) {
            // Initialize with old data
            console.debug('Loading data for edit...', oldData);

            // Try to find action area and subarea ids from arrays.
            // NOTE: This is needed because the API response is not consistent with the frontend.
            const foundActionAreaId = oldData.workActionAreaId ||
                (Array.isArray(oldData.work_action_areas) && oldData.work_action_areas.length > 0
                    ? oldData.work_action_areas[0].id
                    : null);
            const foundActionSubareaId = oldData.workActionSubareaId ||
                (Array.isArray(oldData.work_action_subareas) && oldData.work_action_subareas.length > 0
                    ? oldData.work_action_subareas[0].id
                    : null);

            // Use reset to populate the form fields with the existing data
            reset({
                species: oldData.species_id,
                action: oldData.action_id,
                status: oldData.status_id,
                feet: oldData.feet,
                notes: oldData.notes,
                workActionAreaId: foundActionAreaId,
                workActionSubareaId: foundActionSubareaId,
            });

            dispatch({ type: 'SET_FORM_DATA', name: 'species', value: oldData.species_id });
            dispatch({ type: 'SET_FORM_DATA', name: 'action', value: oldData.action_id });
            dispatch({ type: 'SET_FORM_DATA', name: 'status', value: oldData.status_id });
            dispatch({ type: 'SET_FORM_DATA', name: 'feet', value: oldData.feet });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionArea', value: foundActionAreaId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: foundActionSubareaId });

            // Trigger form validation manually
            // NOTE: This is needed because the form validation is not triggered automatically when the form is reset.
            trigger();
        }
    }, [isEdit, oldData, reset, dispatch, trigger]);

    /**
     * Work action areas, Work action subareas & Work services (Selects)
     */
    useEffect(() => {
        let areas = planning.work?.work_action_areas || [];
        // console.debug('Areas loaded', areas);
        dispatch({ type: 'SET_FILTERED_ACTION_AREA', filteredActionArea: areas });

        let subareas = planning.work?.work_action_subareas || [];
        // console.debug('Subareas loaded', subareas);
        dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: subareas });
    }, [planning]);

    /**
     * Filter subareas based on current action area selected
     */
    useEffect(() => {
        if (planning?.work?.work_action_subareas) {
            const allSubareas = planning.work.work_action_subareas;
            const filtered = allSubareas.filter(
                (subarea) => subarea.in_action_areas_ids?.includes(state.selectActionArea)
            );
            dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: filtered });

            // Check if current subarea is valid for the new zone
            const currentSubareaValid = filtered.some(subarea => subarea.id === state.selectActionSubarea);

            if (filtered.length === 0) {
                // No subareas available for this zone
                if (state.selectActionSubarea !== 0) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                    updateSharedValue('selectActionSubarea', 0);
                }
            } else if (!currentSubareaValid && state.selectActionSubarea !== 0) {
                // Current subarea is not valid for the new zone, reset it
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                updateSharedValue('selectActionSubarea', 0);
            } else if (currentSubareaValid && state.selectActionSubarea === 0) {
                // No subarea selected but there are valid options, auto-select the first one
                const firstSubarea = filtered[0];
                if (firstSubarea) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: firstSubarea.id });
                    updateSharedValue('selectActionSubarea', firstSubarea.id);
                }
            }
            // If current subarea is valid, keep it (no action needed)
        }
    }, [state.selectActionArea, planning]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={styles.formBox}>

            {/* BEGIN Shared common fields */}
            <WorkActionAreaControl value={getSharedValue('selectActionArea', state.selectActionArea || 0)} list={state.filteredActionArea} handleChange={handleChange} showParent={false} hideSectors={true} />
            <WorkActionSubareaControl value={getSharedValue('selectActionSubarea', state.selectActionSubarea || 0)} list={state.filteredActionSubarea} handleChange={handleChange} />
            {/* END Shared common fields */}

            <Controller
                name="species"
                control={control}
                rules={{}}
                render={({ field, fieldState }) => (
                    <SpeciesSelectControl
                        {...field}
                        value={field.value || ''} // Ensure controlled value
                        onChange={field.onChange} // Hook form validation
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />

            <Controller
                name="action"
                control={control}
                rules={{}}
                render={({ field, fieldState }) => (
                    <ExtraActionSelectControl
                        {...field}
                        value={field.value || ''} // Ensure controlled value
                        onChange={field.onChange} // Hook form validation
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />

            <Controller
                name="status"
                control={control}
                rules={{}}
                render={({ field, fieldState }) => (
                    <ExtraStatusSelectControl
                        {...field}
                        value={field.value || ''} // Ensure controlled value
                        onChange={field.onChange} // Hook form validation
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />

            <Controller
                name="feet"
                control={control}
                rules={{ required: t('Feet is required') }}
                render={({ field, fieldState }) => (
                    <TextField
                        id="feet"
                        label={t('Feet')}
                        type="number"
                        required
                        fullWidth
                        sx={{ mt: 2 }}
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />

            <Controller
                name="notes"
                control={control}
                render={({ field, fieldState }) => (
                    <TextareaAutosize
                        id="notes"
                        minRows={3}
                        placeholder={t('Notes')}
                        fullWidth
                        style={{ width: '100%', marginTop: '16px', padding: '10px' }}
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />

            <Button disabled={!isValid} variant="contained" color="primary" type="submit">
                {isEdit ? t('Update') : t('Add')}
            </Button>
        </Box>
    );
};

AddExtrasForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    planning: PropTypes.object.isRequired,
    oldData: PropTypes.object,
};

export default AddExtrasForm;